import './DeviceGroupsList.scss';

import React, { useEffect, useState } from 'react';
import DataTable from 'src/component/UI/DataTable';
import { useSelector, useDispatch } from 'react-redux';
import DeviceLinkCombo from 'src/component/UI/DeviceLinkCombo';
import PropTypes from 'prop-types';

import { deleteDeviceGroup, expandDeviceGroupDevices, updateDeviceGroup } from 'src/module/groupDevices/action';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import EditIcon from '@mui/icons-material/EditSharp';
import { pushHistory } from 'src/module/navigation/action';
import { Radio, Tooltip } from '@mui/material';
import { getTablePagePreferences } from 'src/module/authentication/selector';
import { fetchDeviceGroups } from 'src/module/groupDevices/action';
import { getDeviceGroups, getExpandingDeviceGroups, getLoadingDeviceGroups, getNumDeviceGroupsPages, getTotalDeviceGroups } from 'src/module/groupDevices/selector';
import PermissionFence from 'src/component/PermissionFence';
import { useTranslation } from 'src/i18n';
import EditableCell from 'src/component/UI/EditableCell';

export default function DeviceGroupsList ({ forMassProgram, onSelect, selectedGroup }) {
  const { t, tPlural } = useTranslation();
  const dispatch: any = useDispatch();
  const userPagePreference = useSelector(getTablePagePreferences('batch-device-groups'));

  const groups: any = useSelector(getDeviceGroups);

  const numPages: any = useSelector(getNumDeviceGroupsPages);
  const totalGroups: any = useSelector(getTotalDeviceGroups);
  const loading: any = useSelector(getLoadingDeviceGroups);
  const expandingDevices: any = useSelector(getExpandingDeviceGroups);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(userPagePreference);
  const [filter, setFilter] = useState(null);
  const [sort, setSort] = useState(null);

  // The API caps `devices` (see LIST_MAX_DEVICES in service/api/deviceGroups) and reports the
  // group's full membership in `count`, so the rest is fetched only when the user asks for it
  // rather than shipped with every list load.
  const devicesCol = (devices, count, id) => {
    if (!devices || !devices.size) {
      return [];
    }
    const remaining = (count ?? devices.size) - devices.size;
    return <>
      {devices.map((device, index) => (<div key={index} className='alarm-list'><DeviceLinkCombo device={device} /></div>))}
      {remaining > 0 ? <div
        className='device-group-show-all'
        data-testid={`show-all-devices-${id}`}
        onClick={(e) => { e.stopPropagation(); dispatch(expandDeviceGroupDevices(id)); }}
      >{tPlural('deviceGroups.plusMore', remaining)}</div> : null}
    </>;
  };
  const deleteRec = (e, id) => {
    e.stopPropagation();
    dispatch(deleteDeviceGroup(id));
  };

  const goToEditGroup = (id) => {
    dispatch(pushHistory(`/devices/list/edit-group/${id}`)); //
  };

  const editRec = (e, group) => {
    e.stopPropagation();
    goToEditGroup(group.get('id'));
  };

  const handleUpdateGroupName = (groupId, groupName) => {
    dispatch(updateDeviceGroup(groupId, groupName, null, false));
  };
  const columnOptions: any = [
    {
      id: 'name',
      Header: t('deviceGroups.groupName'),
      accessor: (row: any) => row?.getIn(['group_name']) || '',
      Cell: (row: any) => <div>
        <EditableCell  permission='edit-device' maxInputLength={255} onChange={(newName) => handleUpdateGroupName(row.cell.row.original.get('id'), newName)} value={row.cell.value} multiLine={true} emptyCellLabel={t('deviceGroups.groupName')}/>
      </div>,
      sortable: true
    },
    {
      id: 'devices',
      Header: t('deviceGroups.devices'),
      accessor: (row: any) => row.get('devices'),
      Cell: (row: any) => devicesCol(row.cell.value, row.row.original.get('count'), row.row.original.get('id')),
      disableSortBy: true
    },

  ];

  if (!forMassProgram) {
    columnOptions.push(  {
      id: 'actions',
      Header: '',
      accessor: (row: any) => row.id,
      disableSortBy: true,
      Cell: (row: any) => <div className='device-group-actions'>
        <PermissionFence can={['edit-device']} >
          <>
            <Tooltip title={t('deviceGroups.editGroup')} placement="top">
              <div data-testid={`edit-device-group-${row.row.original.get('id')}`} onClick={(e) => editRec(e, row.row.original)}>
                <EditIcon className='device-group-action-icon device-group-edit-icon' />
              </div>
            </Tooltip>
            <Tooltip title={t('deviceGroups.deleteGroup')} placement="top">
              <div data-testid={`delete-device-group-${row.row.original.get('id')}`} onClick={(e) => deleteRec(e, row.row.original.get('id'))}>
                <DeleteIcon className='device-group-action-icon device-group-delete-icon' />
              </div>
            </Tooltip>
          </>
        </PermissionFence>
      </div>
    });
  } else {
    columnOptions.push({
      id: 'actions',
      Header: '',
      accessor: (row: any) => row.id,
      disableSortBy: true,
      Cell: (row: any) => <div style={{ display: 'flex', justifyContent: 'space-around' }}>
        <Radio
          checked={selectedGroup === row.row.original.get('id')}
          onChange={() => {onSelect(row.row.original.get('id'));}}
          value={row.id}
          onClick={(e) => {e.stopPropagation();}}
          name="radio-buttons-device-groups"
        />
      </div>
    });
  }
  useEffect(() => {
    dispatch(fetchDeviceGroups({ page, pageSize, filter, sort }));
  }, [page, pageSize, filter, sort]);



  const fetchPage = (_page, _pageSize, _filter, _sort) => {
    setPage(_page);
    setPageSize(_pageSize);
    setFilter(_filter);
    setSort(_sort);
  };
  const handleCellClick = (row) => {
    if (onSelect) {
      onSelect(row.original.get('id'));
    }
  };

  return (
    <div className='device-groups' >
      <DataTable
        rowClassName={(row: any) => expandingDevices.has(row.original.get('id')) ? 'device-group-row-loading' : null}
        filterable={forMassProgram}
        filterLabel={t('deviceGroups.searchGroups')}
        handleCellClick={handleCellClick}
        columns={columnOptions}
        data={groups}
        pagination={true}
        numPages={numPages}
        totalRecords={totalGroups}
        loading={loading}
        fetchPage={fetchPage}
        tableId='batch-device-groups'
        columnWidths={['30%', '60%', '10%']}
        hoverBorder={forMassProgram}
        selectedRowId={selectedGroup}
      />
    </div>
  );
}

DeviceGroupsList.propTypes = {
  forMassProgram: PropTypes.bool,
  onSelect: PropTypes.func,
  selectedGroup: PropTypes.string
};

DeviceGroupsList.defaultProps = {
  forMassProgram: false,
  onSelect: () => {},
  selectedDevices: null
};
